import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { trackEvent } from '../utils/analytics';

function PopupModal({
  isVisible,
  onClose,
  onOutsideClick,
  onSubmit,
  countryList,
  selectedCountry,
  setSelectedCountry,
  prefilledData,
}) {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [nickname, setNickname] = useState(prefilledData?.nickname || '');
  const [email, setEmail] = useState(prefilledData?.email || '');
  const [message, setMessage] = useState(prefilledData?.message || '');
  const [isImageExpanded, setIsImageExpanded] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
      try {
        trackEvent('image_selected', {
          method: 'drag_drop',
          mime_type: files[0].type || undefined,
          size_kb: files[0].size ? Math.round(files[0].size / 1024) : undefined,
        });
      } catch (_) { }
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
      try {
        trackEvent('image_selected', {
          method: 'file_input',
          mime_type: files[0].type || undefined,
          size_kb: files[0].size ? Math.round(files[0].size / 1024) : undefined,
        });
      } catch (_) { }
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!isVisible) return;
    setSelectedFile(null);
    setNickname(prefilledData?.nickname || '');
    setEmail(prefilledData?.email || '');
    setMessage(prefilledData?.message || '');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [isVisible, prefilledData]);

  // Control body scrolling when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className="overlay" id="popup" onClick={onOutsideClick}>
      <div className="modal">
        <button className="close-btn" onClick={onClose}>&times;</button>
        <h2>
          {currentTheme.id === 'iraq' 
            ? t('popup.iraq.title') 
            : currentTheme.id === 'malaysia'
            ? t('popup.malaysia.title')
            : t('popup.title')
          }
        </h2>
        {/* <p>
          {currentTheme.id === 'iraq' ? (
            <>
              {t('popup.iraq.subtitle.line1')} <br />
              {t('popup.iraq.subtitle.line2')}
            </>
          ) : currentTheme.id === 'malaysia' ? (
            <>
              {t('popup.malaysia.subtitle.line1')} <br />
              {t('popup.malaysia.subtitle.line2')}
            </>
          ) : (
            <>
              {t('popup.subtitle.line1')} <br />
              {t('popup.subtitle.line2')}
            </>
          )}
        </p> */}

        <div
          className="dropzone"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={handleClickUpload}
        >
          {selectedFile ? (
            <div style={{ textAlign: 'center' }}>
              <p style={{ marginBottom: '1rem' }}>
                {t('popup.dropzone.selected', { defaultValue: '✅ Selected: {{name}}', name: selectedFile.name })}
              </p>
              <div 
                style={{ 
                  cursor: 'pointer',
                  position: 'relative',
                  display: 'inline-block'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsImageExpanded(true);
                }}
              >
                <img
                  src={URL.createObjectURL(selectedFile)}
                  alt="Selected file preview"
                  style={{
                    maxWidth: '200px',
                    maxHeight: '200px',
                    borderRadius: '8px',
                    border: '2px solid rgba(0, 212, 255, 0.5)',
                    cursor: 'pointer'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  opacity: 0,
                  transition: 'opacity 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.opacity = 1}
                onMouseLeave={(e) => e.target.style.opacity = 0}
                >
                  Click to expand
                </div>
              </div>
            </div>
          ) : (
            <p>
              {t('popup.dropzone.prompt')}
            </p>
          )}
        </div>
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <div className="field-with-counter">
          <input
            type="text"
            name="nickname"
            placeholder={t('popup.inputs.nickname')}
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            onBlur={() => {
              if (nickname && nickname.trim().length > 0) {
                try { trackEvent('name_filled', { length: nickname.trim().length }); } catch (_) { }
              }
            }}
            maxLength={50}
          />
          <div className="char-counter">
            {nickname ? nickname.length : 0}/50
          </div>
        </div>
        <input
          type="email"
          name="email"
          placeholder={t('popup.inputs.email')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => {
            if (email && email.trim().length > 0) {
              try { trackEvent('email_filled', { length: email.trim().length }); } catch (_) { }
            }
          }}
        />
        <div className="field-with-counter">
          <textarea
            rows="3"
            placeholder={t('popup.inputs.message')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onBlur={() => {
              if (message && message.trim().length > 0) {
                try { trackEvent('message_filled', { length: message.trim().length }); } catch (_) { }
              }
            }}
            maxLength={1000}
          ></textarea>
          <div className="char-counter">
            {message ? message.length : 0}/1000
          </div>
        </div>
        <select
          name="country"
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
        >
          <option value="">{t('popup.select.country')}</option>
          {Array.isArray(countryList) && countryList.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </select>
        {/* Original submit CTA kept for reuse
        <button
          className='submit-button'
          onClick={() => {
            try {
              trackEvent('submit_click', {
                has_image: !!selectedFile,
                has_email: !!(email && email.trim().length > 0),
                has_name: !!(nickname && nickname.trim().length > 0),
                has_message: !!(message && message.trim().length > 0),
              });
            } catch (_) { }
            onSubmit(selectedFile, nickname, email, message);
          }}
        >
          {currentTheme.id === 'iraq'
            ? t('popup.iraq.submit')
            : currentTheme.id === 'malaysia'
              ? <>{t('popup.malaysia.submit')} <span className="price-suffix">(RM 6.9 )</span></>
              : t('popup.submit')
          }
        </button>
        */}

        {/* Stripe Buy Button as popup CTA */}
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
          <stripe-buy-button
            buy-button-id="buy_btn_1SEBZrGIXmQ4fxZP68NIVv8q"
            publishable-key="pk_live_51MvfGwGIXmQ4fxZPFaN8ENucMWsBQVXOM3cAzIAqhZxuDFVWxQq318ofaJCDUMCk03lUtAFtfKJDu0oe3HZcRMSz00tcAiUAa2"
          >
          </stripe-buy-button>
        </div>
      </div>
      
      {/* Expanded Image Modal */}
      {isImageExpanded && selectedFile && (
        <div className="overlay">
          <div className="modal image-expanded">
            <button 
              className="close-expanded"
              onClick={() => setIsImageExpanded(false)}
            >
              ×
            </button>
            <div className="modal-content">
              <img
                src={URL.createObjectURL(selectedFile)}
                alt="Expanded view"
                className="expanded-image"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PopupModal;
